import { isPlainObject, isArray, forOwn, set, merge, sortBy, cloneDeep } from 'lodash-es';
/**
   To apply the oasisAppConfig overrides
 */
export const updateOasisConfigOverrides = (appConfig, overrideConfig) => {
    const overrideKeys = appConfig?.fields?.config?.fields?.overrideKeys;
    overrideKeys?.forEach((overrideKey) => {
        if (overrideConfig[overrideKey]) {
            if (typeof overrideConfig[overrideKey] === 'object' &&
                typeof appConfig.fields.config.fields[overrideKey] === 'object') {
                merge(appConfig.fields.config.fields[overrideKey], overrideConfig[overrideKey]);
            }
            else {
                appConfig.fields.config.fields[overrideKey] =
                    overrideConfig[overrideKey];
            }
        }
    });
    ['theme', 'locale', 'logo'].forEach((jsonToOverride) => {
        if (overrideConfig && overrideConfig[jsonToOverride]) {
            appConfig.fields[jsonToOverride] = overrideConfig[jsonToOverride];
        }
    });
    if (overrideConfig?.clientIcons) {
        appConfig.fields.icons.forEach((baseIcon, index) => {
            overrideConfig.clientIcons.forEach((overrideIcon) => {
                if (overrideIcon.fields.name === baseIcon.fields.name) {
                    appConfig.fields.icons[index] = overrideIcon;
                }
            });
        });
    }
    return appConfig;
};
/**
 * To get the oasis App config overrides list based on env and client(rebrands)
 */
export const getOasisConfigOverrides = (isWlProd, envOverridesConfig, clientOverridesConfig, clientId, appId, contentfulEnv) => {
    if (isWlProd) {
        return {
            fields: [],
        };
    }
    if (clientId && appId) {
        return clientOverridesConfig.find((client) => client.fields.clientId === clientId &&
            client.fields.appId === appId &&
            client.fields.clientEnv === contentfulEnv);
    }
    return envOverridesConfig?.find((env) => env.fields.clientEnv === contentfulEnv);
};
/**
 * To update the domain url in final config
 */
export const updateDomainUrl = (appConfig, featureApps = [], propKey, localAppsList, domainName, isHybrid) => {
    const processProps = (processConfig) => {
        if (!processConfig ||
            (typeof processConfig !== 'object' && !Array.isArray(processConfig)))
            return false;
        if (processConfig.hasOwnProperty(propKey)) {
            // Get the release feature config details
            const featureConfig = featureApps.find((feature) => feature?.fields?.name === processConfig[propKey]) || {};
            const featureName = featureConfig?.fields?.name || '';
            const featureVersion = featureConfig?.fields?.version || '';
            const featureLocalIp = featureConfig?.fields?.localIp || '';
            // Construct the feature endpoint domain url
            let domainUrl = '';
            if (isHybrid) {
                domainUrl = featureName || '';
            }
            else if (localAppsList.length &&
                localAppsList.indexOf(featureName) !== -1 &&
                featureLocalIp) {
                domainUrl = featureLocalIp.endsWith('/')
                    ? featureLocalIp.slice(0, -1)
                    : featureLocalIp;
            }
            else if (domainName && featureName && featureVersion) {
                domainUrl = `https://${domainName}/${featureName}/${featureVersion}`;
            }
            // Update the app config source with feature endpoint url if exists
            if (domainUrl) {
                processConfig[propKey] = `${domainUrl}/remote.js`;
            }
        }
        // Handle the array type config
        if (Array.isArray(processConfig) && processConfig.length) {
            //  Process the sub configs under config array
            processConfig.forEach((conf) => processProps(conf));
        }
        // Handle the non-array or object type properties
        //  Process the sub configs under config object
        else {
            // eslint-disable-next-line no-restricted-syntax
            for (const [key, value] of Object.entries(processConfig)) {
                processProps(typeof value === 'object' || Array.isArray(value) ? value : key);
            }
        }
    };
    // Process the app config
    processProps(appConfig.fields);
    return appConfig;
};
/**
 * Get the contentful override config's
 */
export const getContentfulOverrides = (idList, clientOverrides, env) => {
    const overrideConfig = clientOverrides.find(config => config.fields?.appId === env.appId &&
        config.fields?.clientId === env.clientId &&
        config.fields?.clientEnv === env.contentfulEnvironment);
    if (!overrideConfig) {
        return undefined;
    }
    const clonedFields = cloneDeep(overrideConfig.fields);
    clonedFields.overrideConfig = cloneDeep(clonedFields.overrideConfig || []);
    return clonedFields.overrideConfig.find(override => idList.includes(override?.sys?.id));
};
/**
 * To override any contentful config
 */
export const updateContentfulOverrides = (obj, targetKey, clientOverrides, env) => {
    const resultPath = [];
    function recursiveSearch(object, currentPath) {
        forOwn(object, (value, key) => {
            const newPath = currentPath.concat(key);
            if (key === targetKey) {
                resultPath.push({
                    path: currentPath.join('.'),
                    value,
                });
            }
            else if (isPlainObject(value) || isArray(value)) {
                recursiveSearch(value, newPath);
            }
        });
    }
    recursiveSearch(obj, []);
    const sortedPath = sortBy(resultPath, 'path');
    const newObj = cloneDeep(obj);
    sortedPath.forEach(({ path, value }) => {
        const configToOverride = getContentfulOverrides(value, clientOverrides, env);
        if (configToOverride?.fields) {
            set(newObj, path, configToOverride.fields);
        }
    });
    return newObj;
};
